import React, {useEffect, useRef} from 'react';
import L from "leaflet";
import {Marker, useMap} from "react-leaflet";
import {ConfigApi, PlaceApi, StoreConfigCoordinatesApi} from "../../../../../services/Api/types";
import * as ReactDOMServer from "react-dom/server";
import {ReactComponent as MarkerSVG} from "../../../../../../images/icons/marker-solid.svg";
import {useTranslation} from "react-i18next";
import {createKeyDownHandler} from "../../../../../utils/keyDownHanlder";

interface Props {
    mapCoordinates: (coordinates: StoreConfigCoordinatesApi) => [number, number];
    place: PlaceApi;
    selectedPlace: PlaceApi | undefined;
    defaultConfig: ConfigApi | undefined;
    handleMarkerClick: (place: PlaceApi,) => void;
}

const MultiStoreMapComponentMarker = ({
                                          mapCoordinates,
                                          place,
                                          selectedPlace,
                                          defaultConfig,
                                          handleMarkerClick
                                      }: Props) => {
    const markerRef = useRef<L.Marker | null>(null);
    const iconFromConfig = defaultConfig?.layout.ico_link?.small;
    const {t} = useTranslation();
    const map = useMap();

    useEffect(() => {
        const element = markerRef.current?.getElement();
        if (!element) return;

        let lastInteractive: HTMLElement | null = null;

        if (element.tabIndex !== -1) {
            element.tabIndex = -1;
        }

        const onKeyDown = createKeyDownHandler<HTMLElement>({
            keys: ["Enter", " "],
            preventDefault: true,
            action: () => handleMarkerClick(place)
        })

        const onFocus = () => {
            const latlng = markerRef.current?.getLatLng();
            if (latlng) {
                map.flyTo(latlng, map.getZoom());
            }
        };

        const attachListeners = () => {
            const newInteractive = element.querySelector(".marker-place-icon") as HTMLElement | null;

            if (lastInteractive && lastInteractive !== newInteractive) {
                lastInteractive.removeEventListener("keydown", onKeyDown);
                lastInteractive.removeEventListener("focus", onFocus);
            }

            if (newInteractive && newInteractive !== lastInteractive) {
                newInteractive.tabIndex = 0;
                newInteractive.setAttribute("role", "button");
                newInteractive.addEventListener("keydown", onKeyDown);
                newInteractive.addEventListener("focus", onFocus);
                lastInteractive = newInteractive;
            }
        };

        attachListeners();

        const observer = new MutationObserver(() => {
            if (element.tabIndex !== -1) {
                element.tabIndex = -1;
            }
            attachListeners();
        });

        observer.observe(element, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ["tabindex"],
        });

        return () => {
            if (lastInteractive) {
                lastInteractive.removeEventListener("keydown", onKeyDown);
                lastInteractive.removeEventListener("focus", onFocus);
            }
            observer.disconnect();
        };
    }, [place]);

    const selectedIcon = L.divIcon({
        className: "",
        html: ReactDOMServer.renderToString(
            <div className="marker-place-icon active" tabIndex={0} role="button"
                 aria-label={`${t("modules.multistore.field.selected_restaurant.title")} ${place.name}`}>
                {iconFromConfig ? <img alt={t("modules.multistore.field.store_icon.title")} src={iconFromConfig}/> :
                    <MarkerSVG className="icon" aria-hidden="true"/>}
            </div>
        ),
    });
    const icon = L.divIcon({
        className: "",
        html: ReactDOMServer.renderToString(
            <div className="marker-place-icon" tabIndex={0} role="button"
                 aria-label={`${t("modules.multistore.field.restaurant.title")} ${place.name}`}>
                {iconFromConfig ? <img alt={t("modules.multistore.field.store_icon.title")} src={iconFromConfig}/> :
                    <MarkerSVG className="icon" aria-hidden="true"/>}
            </div>
        ),
    });

    return (
        <Marker position={mapCoordinates(place.coordinates)}
                icon={selectedPlace && selectedPlace.domain == place.domain ? selectedIcon : icon}
                eventHandlers={{
                    click: () => handleMarkerClick(place),
                }}
                ref={markerRef}
        >
        </Marker>
    )
}

export default MultiStoreMapComponentMarker;